import { Dispatch, useCallback, useMemo, useState } from 'react';
import { path, equals, intersperse } from 'ramda';
import {
  BusinessDataFormUpdateType,
  IBusinessDataFormContext,
  useBusinessDataFormContext,
  useNonNullableBusinessDataFormContext,
} from '../Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import Logger from '../../../../Utils/Logger';
import { IClientBusinessModel } from '../../../../Types/ClientModelTypes';
import {
  INodeData,
  makeNodeQuery,
} from '../../../../Components/QueryComponents/NodeQuery/NodeQuery';
import {
  BusinessDataFormAction,
  IFieldValidations,
  IFormData,
} from '../Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import { ISuggestion } from '../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import { useActiveDBCollectionQuery } from '../../../../Components/QueryComponents/ActiveDBCollectionQuery/ActiveDBCollectionQuery';
import { emptyUrvalNoPages } from '../../../../Components/Urval/Utils/urvalUtils';
import { ICustomValidValues } from '../../../../Types/ColumnDefType';
import mergeSort from '../../../../Utils/mergeSort';
import { findHeaderColumns } from '../Const/BusinessConfigs';
import { getEditedAssociation } from '../../../../Model/BusinessModelUtils';
import { ICollectionDataType } from '../Components/BusinessCollectionComponents/Queries/BusinessCollectionQuery';
import { useQuery } from '@apollo/client';

export interface IUseAssociationValuesResult {
  options: ISuggestion[];
  value: any;
  isEdited: boolean;
  model: IClientBusinessModel | undefined;
  validations: IFieldValidations;
  editMode: boolean;
  formStateDispatch?: Dispatch<BusinessDataFormAction>;
  update?: BusinessDataFormUpdateType;
  data?: INodeData;
  editCancelled: boolean;
  touched: boolean;
}
/**
 * Om två associationvalues är 'lika'.
 * Använder equals på vissa fält för att kunna fortsätta använda det gamla värdet och slippa omrendering av objektlänkar när de ändrade fälten har beräknats till ett likadant värde.
 *
 * Vid ändringar i interfacet behöver den här funktionen uppdateras så alla fält testas.
 */
const isDifferentAssociationValues = (
  values: IUseAssociationValuesResult,
  newValues: IUseAssociationValuesResult
) => {
  return (
    !equals(values.options, newValues.options) ||
    !equals(values.value, newValues.value) ||
    values.isEdited !== newValues.isEdited ||
    values.model !== newValues.model ||
    !equals(values.validations, newValues.validations) ||
    values.editMode !== newValues.editMode ||
    values.formStateDispatch !== newValues.formStateDispatch ||
    values.update !== newValues.update ||
    values.data !== newValues.data ||
    values.editCancelled !== newValues.editCancelled ||
    values.touched !== newValues.touched
  );
};

const getSuggestionName = (
  headerDefs: string[],
  collectionData: ICollectionDataType | undefined
): string =>
  headerDefs
    .reduce<string[]>((acc, def) => {
      const value: string = collectionData?.[def];
      if (value) {
        acc.push(value);
      }
      return acc;
    }, [])
    .join(' - ');

export const getAssociationOptions = (
  data: ICollectionDataType[] | null,
  headerDefs: string[],
  currentValue: ICollectionDataType | undefined,
  currentValueId: string | undefined,
  getValidValues: (options: ISuggestion[]) => ISuggestion[],
  nodeQueryData: any,
  skipSort?: boolean
): ISuggestion[] => {
  const optionsFromData: ISuggestion[] =
    data != null
      ? data.map(collectionItem => ({
          name: getSuggestionName(headerDefs, collectionItem),
          value: collectionItem.id,
          data: collectionItem,
        }))
      : [];

  currentValueId = currentValueId ?? currentValue?.id;

  const selectedOption: ISuggestion | undefined = currentValueId
    ? optionsFromData.find(
        optionFromData => optionFromData.value === currentValueId
      )
    : undefined;

  const validOptions = getValidValues(optionsFromData);

  if (currentValueId) {
    const selectedOptionExistsInValidOptions = validOptions.find(
      option => option.value === currentValueId
    );
    if (!selectedOptionExistsInValidOptions) {
      const suggestionNameFromCurrentValue = currentValue
        ? getSuggestionName(headerDefs, currentValue)
        : undefined;
      if (selectedOption) {
        validOptions.push(selectedOption);
      } else if (suggestionNameFromCurrentValue && currentValue) {
        const suggestionFromCurrentValue: ISuggestion | undefined = {
          name: suggestionNameFromCurrentValue,
          value: currentValue.id,
          data: currentValue,
        };
        validOptions.push(suggestionFromCurrentValue);
      } else if (nodeQueryData) {
        const suggestionFromNodeQuery: ISuggestion | undefined = {
          name: getSuggestionName(headerDefs, nodeQueryData.node),
          value: nodeQueryData.node?.id,
          data: nodeQueryData.node,
        };
        validOptions.push(suggestionFromNodeQuery);
      }
    }
  }
  if (skipSort) return validOptions;
  return mergeSort(
    validOptions,
    (a, b) => a.name?.localeCompare(b.name, 'sv') || 0
  );
};

export const useOptions = (
  collectionName: string,
  headerDefs: string[],
  currentValue: ICollectionDataType | undefined,
  currentValueId: string | undefined,
  open: boolean,
  validValues?: ICustomValidValues,
  formLoading?: boolean
): ISuggestion[] => {
  const attributeDefs = headerDefs.concat(validValues?.dataDependencies ?? []);
  const { data: collectionItems, loading: collectionItemsLoading } =
    useActiveDBCollectionQuery({
      collectionName: collectionName,
      queryName: 'options' + collectionName,
      attributeDefs,
      urval: validValues?.skipDefaultValues
        ? null
        : validValues?.urval ?? emptyUrvalNoPages,
      skip: formLoading || validValues?.skipDefaultValues,
    });

  currentValueId = currentValueId ?? currentValue?.id;

  const formContext = useBusinessDataFormContext();

  const query = useMemo(
    () => makeNodeQuery(collectionName, attributeDefs),
    [collectionName, attributeDefs]
  );
  const selectedOptionExistsInData = collectionItems?.some(
    collectionItem => collectionItem.id === currentValueId
  );
  const hasSuggestionName = currentValue
    ? Boolean(getSuggestionName(headerDefs, currentValue))
    : undefined;
  const skipNodeQuery =
    !currentValueId || selectedOptionExistsInData || hasSuggestionName;
  const { data: nodeQueryData, loading: nodeQueryLoading } = useQuery(
    query.unsafelyUnwrap(),
    {
      variables: { id: currentValueId },
      skip: skipNodeQuery,
    }
  );

  const getValidValues = useCallback(
    (options: ISuggestion[]) => {
      return validValues?.validValuesFunction && formContext
        ? validValues.validValuesFunction(
            options,
            formContext?.formState,
            validValues.useParentFormState
              ? formContext.parentFormContext?.formState
              : undefined
          )
        : options;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      validValues,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      validValues && formContext?.formState,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      validValues &&
        validValues.useParentFormState &&
        formContext?.parentFormContext?.formState,
    ]
  );

  const options = useMemo(() => {
    return getAssociationOptions(
      collectionItems,
      headerDefs,
      currentValue,
      currentValueId,
      getValidValues,
      nodeQueryData,
      validValues?.skipSort
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentValueId, collectionItemsLoading, nodeQueryLoading]);
  return options;
};

export const getAssociationTouched = (
  associationPath: string,
  formStateTouched: IFormData | undefined
): boolean => {
  const touchedPath = [
    '_Associations',
    ...intersperse('_Associations', associationPath.split('.')),
  ];
  return Boolean(path(touchedPath, formStateTouched));
};

const getAssociationValues = (
  formContext: IBusinessDataFormContext<INodeData>,
  attributePath: string,
  options: ISuggestion[]
): IUseAssociationValuesResult => {
  if (formContext) {
    //validering
    const validations = formContext.formState.validation.filter(validation =>
      typeof validation === 'object'
        ? (validation.fieldName === attributePath &&
            (!validation.path || validation.path.length === 0)) ||
          validation?.path?.[0] === attributePath
        : false
    );

    const touched = getAssociationTouched(
      attributePath,
      formContext.formState.touched
    );

    const currentValue =
      formContext.formState.currentValues?._Associations?.[attributePath]?.id;

    const isEdited = getEditedAssociation(
      attributePath,
      formContext.formState.values,
      'id',
      formContext.formState.loadedValues,
      true
    );
    return {
      options,
      value: currentValue,
      isEdited: isEdited,
      validations,
      editMode: formContext.formState.editMode,
      model: formContext.model,
      formStateDispatch: formContext.formStateDispatch,
      update: formContext.update,
      data: formContext.data,
      editCancelled: formContext.formState.editCancelled,
      touched,
    };
  } else {
    Logger.error(
      new Error('Attributvärde kan inte hämtas utanför BusinessDataFormContext')
    );
    return {
      options: [],
      value: undefined,
      isEdited: false,
      validations: [],
      editMode: false,
      model: undefined,
      formStateDispatch: undefined,
      editCancelled: false,
      touched: false,
    };
  }
};

/**
 * Hook som beräknar de värden som behövs i en ObjektLank
 * Används för att undvika omrenderingar i underliggande komponentträd
 */
export const useAssociationValues = (
  associationPath: string,
  collection: IClientBusinessModel | undefined,
  headerDefs: string[] | undefined,
  validValues: ICustomValidValues | undefined,
  open: boolean,
  formLoading?: boolean
): IUseAssociationValuesResult => {
  const formContext = useNonNullableBusinessDataFormContext();
  const associatedCollectionName = (collection ?? formContext.model)
    .Associations[associationPath].AssociatedCollectionName;
  const optionHeaders =
    headerDefs ?? findHeaderColumns(associatedCollectionName);

  const currentValue =
    formContext.formState.currentValues?._Associations?.[associationPath];

  const options = useOptions(
    associatedCollectionName,
    optionHeaders,
    currentValue,
    currentValue?.id,
    open,
    validValues,
    formLoading
  );
  const [values, setValues] = useState(() =>
    getAssociationValues(formContext, associationPath, options)
  );

  const newValues = getAssociationValues(formContext, associationPath, options);
  if (isDifferentAssociationValues(values, newValues)) {
    setValues(newValues);
    return newValues;
  }
  return values;
};
